/**
 * Remote Logging Service
 *
 * Captures console logs and sends them to the server for remote debugging.
 * Useful for debugging TV apps where direct console access is difficult.
 *
 * Features:
 * - Intercepts console.log, console.error, console.warn
 * - Batches logs to reduce network overhead
 * - Feature flag controlled (VITE_REMOTE_LOGGING)
 * - Auto-includes device info (deviceId, platform, etc.)
 */

export interface RemoteLogEntry {
  timestamp: string;
  level: 'log' | 'error' | 'warn' | 'info' | 'debug';
  message: string;
  args?: unknown[];
  deviceId: string;
  platform: string;
  appVersion: string;
  url?: string;
  stackTrace?: string;
}

interface RemoteLoggerConfig {
  enabled: boolean;
  apiUrl: string;
  deviceId: string;
  platform: string;
  appVersion: string;
  batchSize: number;
  flushInterval: number; // ms
}

class RemoteLoggerService {
  private config: RemoteLoggerConfig;
  private logBuffer: RemoteLogEntry[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private originalConsole: {
    log: typeof console.log;
    error: typeof console.error;
    warn: typeof console.warn;
    info: typeof console.info;
    debug: typeof console.debug;
  };

  constructor() {
    // Store original console methods
    this.originalConsole = {
      log: console.log.bind(console),
      error: console.error.bind(console),
      warn: console.warn.bind(console),
      info: console.info.bind(console),
      debug: console.debug.bind(console),
    };

    // Default config (will be updated by init())
    this.config = {
      enabled: false,
      apiUrl: '',
      deviceId: 'unknown',
      platform: 'unknown',
      appVersion: '1.0.0',
      batchSize: 10,
      flushInterval: 5000, // 5 seconds
    };
  }

  /**
   * Initialize remote logging with configuration
   */
  init(config: Partial<RemoteLoggerConfig>): void {
    this.config = { ...this.config, ...config };

    if (!this.config.enabled) {
      console.log('[RemoteLogger] Remote logging is DISABLED');
      return;
    }

    console.log('[RemoteLogger] 🚀 Initializing remote logging');
    console.log('[RemoteLogger] API URL:', this.config.apiUrl);
    console.log('[RemoteLogger] Device ID:', this.config.deviceId);
    console.log('[RemoteLogger] Platform:', this.config.platform);

    // Intercept console methods
    this.interceptConsole();

    // Start flush timer
    this.startFlushTimer();

    // Log initialization
    this.log('info', '[RemoteLogger] Remote logging initialized', [
      this.config,
    ]);
  }

  /**
   * Intercept console methods to capture logs
   */
  private interceptConsole(): void {
    console.log = (...args: unknown[]) => {
      this.originalConsole.log(...args);
      this.log('log', this.formatMessage(args), args);
    };

    console.error = (...args: unknown[]) => {
      this.originalConsole.error(...args);
      this.log('error', this.formatMessage(args), args, this.getStackTrace());
    };

    console.warn = (...args: unknown[]) => {
      this.originalConsole.warn(...args);
      this.log('warn', this.formatMessage(args), args);
    };

    console.info = (...args: unknown[]) => {
      this.originalConsole.info(...args);
      this.log('info', this.formatMessage(args), args);
    };

    console.debug = (...args: unknown[]) => {
      this.originalConsole.debug(...args);
      this.log('debug', this.formatMessage(args), args);
    };
  }

  /**
   * Format console arguments into a string
   */
  private formatMessage(args: unknown[]): string {
    return args
      .map((arg) => {
        if (typeof arg === 'string') return arg;
        if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
        try {
          return JSON.stringify(arg);
        } catch {
          return String(arg);
        }
      })
      .join(' ');
  }

  /**
   * Get stack trace for errors
   */
  private getStackTrace(): string {
    try {
      throw new Error();
    } catch (e: unknown) {
      const stack = e instanceof Error ? e.stack || '' : '';
      // Remove first 3 lines (Error, getStackTrace, log)
      return stack.split('\n').slice(3).join('\n');
    }
  }

  /**
   * Add log entry to buffer
   */
  private log(
    level: RemoteLogEntry['level'],
    message: string,
    args?: unknown[],
    stackTrace?: string,
  ): void {
    if (!this.config.enabled) return;

    const entry: RemoteLogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      args: args?.length ? args : undefined,
      deviceId: this.config.deviceId,
      platform: this.config.platform,
      appVersion: this.config.appVersion,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      stackTrace,
    };

    this.logBuffer.push(entry);

    // Flush if buffer is full
    if (this.logBuffer.length >= this.config.batchSize) {
      this.flush();
    }
  }

  /**
   * Start periodic flush timer
   */
  private startFlushTimer(): void {
    this.flushTimer = setInterval(() => {
      this.flush();
    }, this.config.flushInterval);
  }

  /**
   * Flush logs to server
   */
  private async flush(): Promise<void> {
    if (this.logBuffer.length === 0) return;

    const logs = [...this.logBuffer];
    this.logBuffer = [];

    try {
      const payload = JSON.stringify({ logs });
      const endpoint = `${this.config.apiUrl}/api/v1/logs`;

      if (typeof navigator !== 'undefined' && 'sendBeacon' in navigator) {
        const blob = new Blob([payload], { type: 'application/json' });
        const ok = navigator.sendBeacon(endpoint, blob);
        if (ok) {
          this.originalConsole.log(
            `[RemoteLogger] ✅ Sent ${logs.length} logs to server (beacon)`,
          );
          return;
        }
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: payload,
        mode: 'cors',
        credentials: 'omit',
        keepalive: true,
      });

      if (!response.ok) {
        // Use original console to avoid infinite loop
        this.originalConsole.error(
          '[RemoteLogger] Failed to send logs:',
          response.status,
          response.statusText,
        );
      } else {
        this.originalConsole.log(
          `[RemoteLogger] ✅ Sent ${logs.length} logs to server`,
        );
      }
      // Neither branch reads the body, and both carry real bytes (the ack on
      // success, a JSON/HTML error otherwise). Dropping a Response without
      // reading or cancelling it keeps Chromium's data pipe alive, reserving
      // ~2MB of address space per request in the 32-bit WebView renderer that
      // is never returned — the renderer-kill root cause measured on the Tab A7
      // (see commit 683464d). Dormant today (VITE_REMOTE_LOGGING unset), but
      // this would be 12 flushes/min once enabled.
      await response.body?.cancel().catch(() => undefined);
    } catch (error) {
      // Use original console to avoid infinite loop
      this.originalConsole.error('[RemoteLogger] Error sending logs:', error);
      // Put logs back in buffer to retry
      this.logBuffer.unshift(...logs);
    }
  }

  /**
   * Manually flush logs immediately
   */
  flushNow(): Promise<void> {
    return this.flush();
  }

  /**
   * Stop remote logging and restore original console
   */
  stop(): void {
    this.originalConsole.log('[RemoteLogger] Stopping remote logging');

    // Flush remaining logs
    this.flush();

    // Stop timer
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }

    // Restore original console
    console.log = this.originalConsole.log;
    console.error = this.originalConsole.error;
    console.warn = this.originalConsole.warn;
    console.info = this.originalConsole.info;
    console.debug = this.originalConsole.debug;

    this.config.enabled = false;
  }
}

// Export singleton instance
export const remoteLogger = new RemoteLoggerService();
export default remoteLogger;
